import { useQuery } from "@tanstack/react-query"

import { getHomeContextApi } from "@/api/home-context"
import { SettingsService } from "@/services/settings-service"

const HOME_CONTEXT_QUERY_KEY = ["home-context"] as const

function getSettingsToken(): string | undefined {
  try {
    return SettingsService.getInstance().token
  } catch {
    return undefined
  }
}

// home_context returns two halves: `graph.*` (Graph/M365-derived) and `helloai.*`
// (Hello AI-only — continue_chats, themes, projects, memory, user_instructions,
// onboarding_completed_at). The backend gates Graph fetches on the user's
// `ui.m365_approval_status` and on the presence of a graph_token, returning an
// empty graph payload otherwise. Declined users still get useful helloai.* data
// for surfaces like the Preferences "what we know" panel. The chat home gates
// the *entire* enriched layout on M365 approval at the page layer (see
// ai-explorer-page.tsx) — when not approved, the plain pre-personalization
// layout renders even if helloai.* has content.
export function useHomeContext(enabled: boolean = true) {
  return useQuery({
    queryKey: HOME_CONTEXT_QUERY_KEY,
    queryFn: async () => {
      const idToken = getSettingsToken()
      const data = await getHomeContextApi(idToken)
      // A missing or unreadable Prism notice is unknown; never coerce it to false.
      return {
        ...data,
        helloai: {
          ...data.helloai,
          about_me_not_applied: data.helloai.about_me_not_applied ?? null,
        },
      }
    },
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  })
}
